import EmptyState from "@/components/EmptyState";
import Table, {
  TableFooter,
  TableControls,
  toTableControlActions,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn,
  buildExpandableCountColumn,
  buildTagColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, generatePath } from "react-router-dom";
import { Path } from "@/router";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import SearchInput from "@/components/SearchInput";
import PersonSideSheet from "@/features/person/components/PersonSideSheet/PersonSideSheet";
import NotesTable from "@/features/global/components/NotesTable";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import useCapabilities from "@/hooks/useCapabilities";
import { NoteNotableValues } from "@/types";
import ErrorBoundary from "@/components/ErrorBoundary";
import { ArchiveGovernanceRoleDialog } from "@/pages/facilities/[facilityId]/people/governance/_ArchiveGovernanceRoleDialog";
import {
  getGovernanceStatusLabel,
  getGovernanceStatusTagType,
  getGovernanceStatusTagStyle,
  mapGovernanceStatus,
  matchesGovernanceSearch,
  matchesGovernanceRoleFilter,
  buildGovernanceRoleFilterOptions,
  getEffectiveGovernanceSearch,
  clearGovernanceFilters
} from "@/pages/facilities/[facilityId]/people/governance/_utils";
import { GovernanceRoleFilter } from "@/pages/facilities/[facilityId]/people/governance/_GovernanceRoleFilter";
import { useGovernanceRoleFilter } from "@/pages/facilities/[facilityId]/people/governance/useGovernanceRoleFilter";
import type { FacilityGovernanceRole } from "@/pages/facilities/[facilityId]/people/governance/types";
import { GovernanceStatus } from "@/pages/facilities/[facilityId]/people/governance/types";
import { AddSectionGovernanceRoleModal } from "./AddSectionGovernanceRoleModal";
import { EditSectionGovernanceRoleModal } from "./EditSectionGovernanceRoleModal";
import { getSectionGovernanceTargetType } from "./getSectionGovernanceTargetType";
import { useCanManageSectionGovernance } from "../useCanManageSectionGovernance";
import { SECTION_GOVERNANCE_ROLES_QUERY } from "./sectionGovernanceQueries";
import { useSectionGovernanceRoleArchival } from "./useSectionGovernanceRoleArchival";

type SectionGovernanceTableProps = {
  sectionId: string;
};

export function SectionGovernanceTable({ sectionId }: SectionGovernanceTableProps) {
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const { selectedRoleIds, onSelect: onSelectRole, onClear: onClearRole } = useGovernanceRoleFilter();
  const urlShowArchived = params.showArchived === "true";
  const [showArchived, setShowArchivedState] = useState(urlShowArchived);

  const setShowArchived = (value: boolean) => {
    setShowArchivedState(value);
    if (value) {
      addSearchParam("showArchived", "true");
    } else {
      deleteSearchParams("showArchived");
    }
  };

  const { data, loading, error } = useQuery(SECTION_GOVERNANCE_ROLES_QUERY, {
    variables: { sectionId }
  });
  const { archiveRole, unarchiveRole, archiving } = useSectionGovernanceRoleArchival(SECTION_GOVERNANCE_ROLES_QUERY);
  const { canManageGovernance, ready: canManageReady } = useCanManageSectionGovernance(data?.section?.legacyId);
  const canManage = canManageReady && canManageGovernance;
  const targetType = getSectionGovernanceTargetType(data?.section);
  const sectionName = data?.section?.name;

  const navigate = useNavigate();
  const location = useLocation();
  const { id: currentUserId } = useCapabilities();
  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);
  const [roleToEdit, setRoleToEdit] = useState<FacilityGovernanceRole | null>(null);
  const [roleToArchive, setRoleToArchive] = useState<FacilityGovernanceRole | null>(null);
  const [isAddGovernanceRoleModalOpen, setIsAddGovernanceRoleModalOpen] = useState(false);
  const [roleToAddNote, setRoleToAddNote] = useState<FacilityGovernanceRole | null>(null);

  const allRoles = useMemo<FacilityGovernanceRole[]>(
    () =>
      (data?.section?.governanceRoles ?? []).map((node) => ({
        ...node,
        status: mapGovernanceStatus(node.status)
      })),
    [data]
  );

  const wasAllArchived = useRef<boolean | null>(null);
  useEffect(() => {
    if (loading || !data) {
      return;
    }

    const allArchived = allRoles.length > 0 && allRoles.every((role) => role.status === GovernanceStatus.ARCHIVED);
    const previouslyAllArchived = wasAllArchived.current;
    wasAllArchived.current = allArchived;

    if (allArchived && previouslyAllArchived !== true) {
      setShowArchivedState(true);
    }
  }, [loading, data, allRoles]);

  const effectiveSearch = getEffectiveGovernanceSearch(searchValue, debouncedSearch, params.search);

  const roles = useMemo(
    () =>
      allRoles
        .filter((role) => showArchived || role.status === GovernanceStatus.ACTIVE)
        .filter((role) => matchesGovernanceSearch(role, effectiveSearch))
        .filter((role) => matchesGovernanceRoleFilter(role, selectedRoleIds)),
    [allRoles, showArchived, effectiveSearch, selectedRoleIds]
  );

  const roleFilterOptions = useMemo(() => buildGovernanceRoleFilterOptions(allRoles), [allRoles]);
  const filterState = {
    role: selectedRoleIds.length > 0
  };

  const activeFilterCount = [showArchived, effectiveSearch, selectedRoleIds.length > 0].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0 || roles.length < allRoles.length;

  function handleClearAllFilters() {
    clearGovernanceFilters({
      setShowArchived: setShowArchivedState,
      setSearchValue,
      deleteSearchParams
    });
  }

  async function handleArchiveGovernanceRole() {
    if (!roleToArchive) {
      return;
    }

    const success = await archiveRole(roleToArchive.id);
    if (success) {
      setRoleToArchive(null);
    }
  }

  async function handleUnarchiveGovernanceRole(role: FacilityGovernanceRole) {
    await unarchiveRole(role.id);
  }

  function buildPersonPath(pgaId: string | null | undefined): Path | null {
    if (!pgaId) {
      return null;
    }
    const returnTo = location.pathname;
    return generatePath(`/people/:pgaId/person/overview?return_to=${encodeURIComponent(returnTo)}`, { pgaId }) as Path;
  }

  if (error) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Governance"
        message="Please try again or contact support if the problem persists."
        className="mt-12"
      />
    );
  }

  const columns = [
    ...buildAvatarDetailsColumn<FacilityGovernanceRole>({
      isCompactLayout,
      getTitle: (row) => row.person.displayName,
      getSubtitle: (row) => row.person.latestProgram?.programType?.name || "",
      subtitleLabel: "Program",
      getPicture: (row) => row.person.profilePhoto,
      header: "Name",
      size: 230
    }),
    buildCopyTextColumn<FacilityGovernanceRole>({
      accessorKey: "person.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.person.pgaId || "",
      shouldShowCopyButton: (row) => !!row.person.pgaId,
      enableSorting: false
    }),
    {
      accessorFn: (row: FacilityGovernanceRole) => row.title.title,
      header: "Role",
      id: "role",
      enableSorting: false
    },
    buildExpandableCountColumn<FacilityGovernanceRole>({
      accessorKey: "notes",
      header: "Notes",
      getCount: (row) => row.notes.length,
      size: 100,
      minSize: 80,
      showExpandIcon: true
    }),
    buildTagColumn<FacilityGovernanceRole>({
      accessorKey: "status",
      header: "Status",
      getTagLabel: (row) => getGovernanceStatusLabel(row.status),
      getTagType: (row) => getGovernanceStatusTagType(row.status),
      getTagStyle: (row) => getGovernanceStatusTagStyle(row.status),
      meta: {
        headerClassName: "hide-header-in-column"
      }
    }),
    buildActionColumn<FacilityGovernanceRole>({
      getActions: (row) => {
        const actions: Action[] = [];

        if (canManage && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Edit Role",
            section: "Manage",
            onClick: () => setRoleToEdit(row)
          });
        }

        if (canManage && row.status === GovernanceStatus.ARCHIVED) {
          actions.push({
            text: "Unarchive Role",
            section: "Manage",
            iconName: "unarchive",
            onClick: () => handleUnarchiveGovernanceRole(row)
          });
        }

        actions.push({
          text: "View Person",
          section: "Actions",
          onClick: () => {
            if (row.person.pgaId) {
              navigate(`/people/${row.person.pgaId}`);
            }
          }
        });

        if (canManage) {
          actions.push({
            text: "Add Note",
            section: "Actions",
            onClick: () => setRoleToAddNote(row)
          });
        }

        if (canManage && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Archive Role",
            iconName: "archive",
            destructive: true,
            onClick: () => setRoleToArchive(row)
          });
        }

        return actions;
      },
      onViewClick: (row) => {
        if (row.person.pgaId) {
          setSelectedPgaId(row.person.pgaId);
          setIsPersonSideSheetOpen(true);
        }
      },
      viewButtonTitle: "View person details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={roles}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        getRowCanExpand={(row) => row.original.notes.length > 0}
        onClickRow={(row) => {
          const personPath = buildPersonPath(row.original.person.pgaId);
          if (personPath) {
            navigate(personPath);
          }
        }}
        columns={columns}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={
              canManage
                ? toTableControlActions([
                    {
                      label: "Add Governance Role",
                      icon: "add",
                      variant: "filled",
                      onClick: () => setIsAddGovernanceRoleModalOpen(true),
                      testId: "add-governance-role-button"
                    }
                  ])
                : []
            }
            filterChips={[
              {
                label: "Show Archived",
                onClick: () => setShowArchived(!showArchived),
                selected: showArchived
              }
            ]}
            useExpandedFilters
            expandedFiltersState={filterState}
            activeFilters={activeFilterCount}
            onClearAllFilters={handleClearAllFilters}
            onSortClick={onSortClick}
          >
            <SearchInput
              data-name="search"
              placeholder="Search Governance Roles"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
            <GovernanceRoleFilter
              name="role"
              options={roleFilterOptions}
              selectedValues={selectedRoleIds}
              onSelect={onSelectRole}
              onClear={onClearRole}
            />
          </TableControls>
        )}
        renderExpandedRow={({ row }) => (
          <NotesTable
            variant="details-panel"
            data={row.original.notes}
            title={`Notes (${row.original.notes.length})`}
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Governance Roles"
            caption="There are no governance roles associated with this section."
            iconName="groups"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="groups" />
        )}
        renderFooter={() => <TableFooter totalRowCount={roles.length} />}
      />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
      />
      <EditSectionGovernanceRoleModal
        open={roleToEdit !== null}
        role={roleToEdit}
        targetType={targetType}
        onClose={() => setRoleToEdit(null)}
        refetchQueries={[SECTION_GOVERNANCE_ROLES_QUERY]}
      />
      {isAddGovernanceRoleModalOpen && (
        <AddSectionGovernanceRoleModal
          open
          sectionId={sectionId}
          targetType={targetType}
          onClose={() => setIsAddGovernanceRoleModalOpen(false)}
          refetchQueries={[SECTION_GOVERNANCE_ROLES_QUERY]}
        />
      )}
      <ArchiveGovernanceRoleDialog
        open={roleToArchive !== null}
        onClose={() => setRoleToArchive(null)}
        onConfirm={handleArchiveGovernanceRole}
        isLoading={archiving}
        personName={roleToArchive?.person.displayName}
        facilityName={sectionName}
        roleLabel={roleToArchive?.title.title}
      />
      {roleToAddNote && (
        <AddEditNoteSideSheet
          open
          onClose={() => setRoleToAddNote(null)}
          notableId={roleToAddNote.id}
          notableType={NoteNotableValues.FACILITY_ASSOCIATION}
          refetchQueries={[SECTION_GOVERNANCE_ROLES_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={roleToAddNote.person.pgaId ?? undefined}
          objectType="person"
          onSaveSuccess={() => setRoleToAddNote(null)}
        />
      )}
    </>
  );
}
